// Patient/Requestor match-state contract (Spec #34101 / F1c, AC17).
// This Spec owns this file and is its only editor - F1d (#34102) reads
// PatientMatchState/RequestorMatchState for its Differs check and
// Confirm-All gating, but does not modify this module.
//
// The `status` discriminator exists because a bare `matched=false` is
// otherwise ambiguous (Business Rule 0/AC10/AC13): awaitingDocument (no
// keys yet - before any search), matching (keys present, search POST in
// flight), matched (green=single or yellow=multiple - see `confidence`),
// noMatch (search returned 0 candidates), serviceUnavailable (search POST
// errored/timed out).

export type MatchConfidence = "none" | "single" | "multiple"; // red | green | yellow
export type MatchStatus =
    | "awaitingDocument"
    | "matching"
    | "matched"
    | "noMatch"
    | "serviceUnavailable";

export interface PatientMatchState {
    readonly status: MatchStatus;
    readonly matched: boolean;
    readonly matchMethod: "None" | "LastNameDob";
    readonly matchCount: number;
    readonly confidence: MatchConfidence;
    readonly differs: Readonly<Record<"firstName" | "lastName" | "address" | "city", boolean>>;
    readonly serviceUnavailable: boolean;
    /** True when the specialist created/entered this value (no-match "Create New") rather than the AI matching it - drives the blue "user-entered" badge vs the green AI-confirmed check (AC9). */
    readonly userEntered: boolean;
    /**
     * AB#35071: the resolved patient's DB id on a single match, so Confirm All can link it
     * (`patientDataid`). Carried HERE, on state the row reports on every change, because the id
     * previously had one channel only - PatientInfoRow's one-shot onSelectPatient effect - and when
     * that was missed or its parent state was cleared, the link was dropped silently and the
     * transaction completed with no patient. Undefined for none/multiple matches.
     */
    readonly patientId?: number;
}

export interface RequestorMatchState {
    readonly status: MatchStatus;
    readonly matched: boolean;
    readonly matchMethod: "None" | "Fax" | "Email";
    readonly matchCount: number;
    readonly confidence: MatchConfidence;
    readonly eTransfer: boolean;
    /** Display-only "N prior requests" (AC7) - never a sort key, never persisted. */
    readonly transactionCount: number;
    readonly differs: Readonly<Record<"name" | "address" | "city", boolean>>;
    readonly serviceUnavailable: boolean;
    /** True when the specialist created/entered this value (no-match "Create New") rather than the AI matching it - drives the blue "user-entered" badge vs the green AI-confirmed check (AC9). */
    readonly userEntered: boolean;
}

// Test 1.6: default states are unmatched/awaitingDocument.
export const DEFAULT_PATIENT_MATCH_STATE: PatientMatchState = {
    status: "awaitingDocument",
    matched: false,
    matchMethod: "None",
    matchCount: 0,
    confidence: "none",
    differs: { firstName: false, lastName: false, address: false, city: false },
    serviceUnavailable: false,
    userEntered: false,
};

export const DEFAULT_REQUESTOR_MATCH_STATE: RequestorMatchState = {
    status: "awaitingDocument",
    matched: false,
    matchMethod: "None",
    matchCount: 0,
    confidence: "none",
    eTransfer: false,
    transactionCount: 0,
    differs: { name: false, address: false, city: false },
    serviceUnavailable: false,
    userEntered: false,
};

// DOB display/wire formatting for the F1c match rows (Spec #34101). The UI shows
// dates as MM/DD/YYYY (classic parity), but the patient-search POST body binds
// to a .NET DateOnly, which parses ISO (yyyy-MM-dd). So we display MM/DD/YYYY
// and convert to ISO on the wire.

/** yyyy-MM-dd (or full ISO datetime) -> MM/DD/YYYY for display. Passes the value through unchanged if it isn't ISO. */
export function toDisplayDob(value?: string): string | undefined {
    if (!value) {
        return value;
    }
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
}

/**
 * MM/DD/YYYY -> yyyy-MM-dd (ISO) for the DateOnly-bound search body. Passes the value through
 * unchanged if it's already ISO or not MM/DD/YYYY.
 *
 * AB#37980 review remediation: an earlier version of this function also normalized a raw
 * 8-digit "10221996" string and returned `undefined` for anything else it didn't recognize.
 * That was reverted - confirmed against both production callers (PatientInfoRow's automated
 * match and its manual inline re-search) that toWireDob never actually receives a raw digit
 * string: the automated path's extractedDob is built via
 * toDisplayDob(new Date(...).toISOString()...), which always yields MM/DD/YYYY (or throws
 * before this function is ever reached), and the manual path's value comes from
 * dateFieldValueToString, which only ever returns '' or MM/DD/YYYY. So the normalization was
 * dead code, and the `undefined` fallthrough was an active hazard: `undefined` is OMITTED from
 * the JSON body, binds to `Dob = NULL`, and reaches MatchPatient(facilityId, lastName, null,
 * mrn) as a genuine surname-only search - and a single-result surname match auto-resolves onto
 * the request with no DOB check at all. Passing unrecognized input through unchanged keeps that
 * from happening.
 *
 * What a '' dob actually does, since the comment here used to say this wrongly and the wrong
 * model is what produced the reverted fix: '' short-circuits below and ships as `"dob": ""`,
 * which the [ApiController] rejects at MODEL BINDING - System.Text.Json cannot convert "" to
 * `DateOnly?` and no converter is registered - so it 400s, never reaching PatientSearchAsync.
 * axios rejects, runSearch lands in its catch, and the row renders "Match service unavailable"
 * plus "Switch to Classic Mode?". So an empty dob does NOT run a surname-only search; it
 * misreports the specialist's own input as a match-service outage. That misattribution is
 * pre-existing and independent of this bug (any empty DOB does it, typed or not) - recorded as
 * a follow-up on AB#37980 rather than fixed here, because the remedies (omit the key, which
 * reopens the surname-only hazard above, or register a DateOnly converter server-side) are both
 * wire-contract changes.
 *
 * See matchState.test.ts for the pinning tests - they characterize this function, they do not
 * exert regression pressure on the defect (its body is unchanged from before this bug).
 */
export function toWireDob(value?: string): string | undefined {
    if (!value) {
        return value;
    }
    const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[1]}-${m[2]}` : value;
}

// AB#35087: rich "How this match was made" tooltip content for the Patient/
// Requestor info-icon, matching master-demo-v6 (e.g. "Searched by last name
// Whitaker + DOB 03/14/1962 — one patient returned, so no tie-break was needed",
// and for requestor the ordered match-rule list). Pure + shared so both rows
// render identical wording and it's unit-testable. The search values shown here
// (last name / DOB / fax / email) are already displayed on the same card, so this
// is on-screen UI to the authorized specialist - NOT logged (see hipaa-compliance).
export interface MatchTooltipContent {
    readonly header: string;
    readonly searchLine: string;
    /** Substrings of searchLine to render bold (the dynamic search values, e.g. the fax number) - matches the mockup's emphasis. */
    readonly emphasize?: readonly string[];
    /** Requestor only: the ordered match-selection rules (tdd-feature-31605 §requestor decision tree). */
    readonly rules?: readonly string[];
    /** Requestor only: 0-based index of the rule the match method satisfied (highlighted green in the mockup). */
    readonly activeRuleIndex?: number;
}

// Ordered requestor match rules (tdd-feature-31605: fax/email -> e-transfer ->
// National -> prior-request count -> best judgment). Prior-request count is a
// tiebreaker here per the demo's own list, shown as displayed metadata elsewhere.
const REQUESTOR_MATCH_RULES: readonly string[] = [
    "Search by fax / email",
    "Prefer e-transfer requestor",
    "Else prefer National requestor",
    "Else highest prior-request count",
    "Else best match by judgment",
];

const countWord = (n: number): string => (n === 1 ? "one" : String(n));

// AB#35087: how the match resolved. selectedFromCount is set only when the
// specialist tie-broke a MULTIPLE result by picking one - in that case the panel
// shows the original returned count + "so you selected this one" (rather than
// pretending it was a clean single). Returns the effective returned count, the
// noun-appropriate plural, and the tie-break clause.
function resolveMatchNarrative(matchCount: number, entity: "patient" | "requestor", selectedFromCount?: number): {
    count: number;
    noun: string;
    tieBreak: string;
    userTieBroke: boolean;
} {
    const userTieBroke = Boolean(selectedFromCount && selectedFromCount > 1);
    const count = userTieBroke ? (selectedFromCount as number) : matchCount;
    const noun = count === 1 ? entity : `${entity}s`;
    const tieBreak = userTieBroke
        ? "so you selected this one"
        : (count === 1 ? "so no tie-break was needed" : "so the top match was selected");
    return { count, noun, tieBreak, userTieBroke };
}

export function buildPatientMatchTooltip(params: {
    lastName?: string;
    dob?: string;
    matchMethod: "None" | "LastNameDob";
    matchCount: number;
    /** Set only when the specialist picked from a MULTIPLE result (the original count returned). */
    selectedFromCount?: number;
}): MatchTooltipContent {
    const parts: string[] = [];
    if (params.lastName) parts.push(`last name ${params.lastName}`);
    if (params.dob) parts.push(`DOB ${params.dob}`);
    const by = params.matchMethod === "LastNameDob" && parts.length ? parts.join(" + ") : "AI extraction";
    const { count, noun, tieBreak } = resolveMatchNarrative(params.matchCount, "patient", params.selectedFromCount);
    const emphasize = params.matchMethod === "LastNameDob"
        ? [params.lastName, params.dob].filter((v): v is string => Boolean(v))
        : [];
    return {
        header: "How this match was made",
        searchLine: `Searched by ${by} — ${countWord(count)} ${noun} returned, ${tieBreak}.`,
        emphasize,
    };
}

export function buildRequestorMatchTooltip(params: {
    fax?: string;
    email?: string;
    matchMethod: "None" | "Fax" | "Email";
    matchCount: number;
    /** Set only when the specialist picked from a MULTIPLE result (the original count returned). */
    selectedFromCount?: number;
}): MatchTooltipContent {
    const matchedValue = params.matchMethod === "Fax" ? params.fax
        : params.matchMethod === "Email" ? params.email
        : undefined;
    const by = params.matchMethod === "Fax" && params.fax ? `fax ${params.fax}`
        : params.matchMethod === "Email" && params.email ? `email ${params.email}`
        : "AI extraction";
    const { count, noun, tieBreak, userTieBroke } = resolveMatchNarrative(params.matchCount, "requestor", params.selectedFromCount);
    // Highlight the rule the resolution used: a specialist tie-break from a multiple
    // result is rule 5 ("best match by judgment", index 4); a fax/email hit is rule 1.
    const activeRuleIndex = userTieBroke ? 4
        : (params.matchMethod === "Fax" || params.matchMethod === "Email") ? 0
        : undefined;
    return {
        header: "How this match was made",
        searchLine: `Searched by ${by} — ${countWord(count)} ${noun} returned, ${tieBreak}.`,
        emphasize: matchedValue ? [matchedValue] : [],
        rules: REQUESTOR_MATCH_RULES,
        activeRuleIndex,
    };
}

/**
 * AB#35087: the "Selected requestor" panel — a requestor the specialist picked from the CLASSIC
 * requestor search, not one the automatic fax/email match resolved.
 *
 * That path (<c>renderCreatedRequestor(selected, "selected")</c>) short-circuits renderValue before
 * renderMatchedRequestor, so it never carried an info icon and QA reported the icon "missing".
 * It cannot reuse buildRequestorMatchTooltip: with no automatic match the matchMethod is "None",
 * which would render "Searched by AI extraction — one requestor returned, so no tie-break was
 * needed" and assert a resolution that never ran.
 *
 * Deliberately omits the ordered match rules. They describe how the engine RANKS candidates; here
 * the specialist chose directly, so listing them would imply a ranking that did not happen. The
 * line also explains the FROM DATABASE pills on the same card — the fields come from the saved
 * record rather than the request document.
 */
export function buildSelectedRequestorTooltip(): MatchTooltipContent {
    return {
        header: "How this requestor was chosen",
        searchLine: "Selected from the requestor search — details come from the saved requestor record, not the document.",
    };
}

/** Flattens a MatchTooltipContent to one string for the icon's aria-label (screen readers). */
export function flattenMatchTooltip(t: MatchTooltipContent): string {
    const rules = t.rules && t.rules.length
        ? ` Match rules: ${t.rules.map((r, i) => `${i + 1}. ${r}`).join("; ")}.`
        : "";
    return `${t.header}. ${t.searchLine}${rules}`;
}

// Server-side MatchConfidence. The API may serialize it either as PascalCase
// strings ("None"/"Single"/"Multiple", System.Text.Json + JsonStringEnumConverter)
// OR as the raw enum integers (0/1/2, when Newtonsoft is the active serializer).
// Accept BOTH so the row derives the right state regardless of the serializer -
// otherwise a numeric confidence falls through to "none" and a real single match
// renders as "No match found" (the candidate grid, keyed off `candidates`, would
// still show the row - the exact symptom this guards against).
type ApiMatchConfidence = "None" | "Single" | "Multiple";

function toClientConfidence(confidence: ApiMatchConfidence | number | undefined): MatchConfidence {
    switch (confidence) {
        case "Single":
        case 1:
            return "single";
        case "Multiple":
        case 2:
            return "multiple";
        default:
            return "none";
    }
}

/** POST /api/v1/intake-intelligence/patient-search candidate shape (PatientCandidate). */
export interface PatientCandidateApiPayload {
    readonly patientId: number;
    readonly firstName?: string;
    readonly lastName?: string;
    /** Date of birth, formatted yyyy-MM-dd server-side for display (PatientCandidate.Dob). */
    readonly dob?: string;
    readonly empi?: number;
    readonly mrn?: string;
}

/** POST /api/v1/intake-intelligence/patient-search response shape (PatientMatchResult). */
export interface PatientMatchApiPayload {
    readonly matched: boolean;
    /** The matched patient record (single match) - drives the rich matched display (name + DOB/MRN/EMPI). */
    readonly patient?: {
        readonly contact?: {
            readonly firstName?: string;
            readonly middleName?: string;
            readonly lastName?: string;
        };
        /** ISO datetime string (e.g. "2000-05-02T00:00:00") from the matched DB record. */
        readonly dob?: string;
        readonly mrn?: string;
        readonly empi?: number;
    };
    readonly matchMethod: "None" | "LastNameDob";
    readonly matchCount: number;
    readonly confidence: ApiMatchConfidence;
    /** Populated for the yellow (Multiple) disambiguation grid (AC9); mirrors RequestorMatchApiPayload.candidates. */
    readonly candidates?: readonly PatientCandidateApiPayload[];
}

/**
 * Write-once dedup key for a resolved single patient match (PatientInfoRow's
 * onSelectPatient effect). Keys off the DB id FIRST (candidates[0].patientId,
 * which PatientManager always populates for a match), mirroring
 * RequestorInfoRow's requestorId-first key.
 *
 * Uses `||` (not `??`) through the whole fallback chain so an EMPTY-STRING empi
 * or mrn is treated as absent: the API serializes a patient with no EMPI as
 * empi:"" (not null), and `""` passes `??` — which collapsed the key to "" and
 * made the write-once skip, so a patient with no EMPI never fired onSelectPatient
 * and its patientDataid was never persisted (Patient Name lost at Confirm All).
 * Returns "" only when there is no matched patient payload at all (the caller
 * already gates on that; the empty return is a defensive backstop).
 */
export function resolvePatientMatchWriteKey(apiResult: PatientMatchApiPayload | undefined): string {
    const p = apiResult?.patient;
    if (!p) {
        return "";
    }
    return String(
        apiResult?.candidates?.[0]?.patientId
        || p.empi
        || p.mrn
        || `${p.contact?.lastName ?? ""}|${p.dob ?? ""}`,
    );
}

/** POST /api/v1/intake-intelligence/requestor-search candidate shape (RequestorCandidate). */
export interface RequestorCandidateApiPayload {
    readonly requestorId: number;
    readonly fullName?: string;
    readonly address1?: string;
    readonly address2?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zipCode?: string;
    readonly eTransfer: boolean;
    readonly national: boolean;
    readonly transactionCount: number;
    /**
     * The ID of the ONE contact of this requestor that came through the search, when
     * exactly one did (AB#39052); absent when the requestor has no contact or several
     * matched. Picking this candidate pins that contact.
     *
     * The ID ONLY. The search is requestor-level and returns no contact demographics
     * (Ali Moll, PR !10513 thread 56655) - every candidate in the list matched on the
     * same fax or email, so the contact's own values are identical across the list and
     * differentiate nothing. `resolveContact` fetches them for the one that is picked.
     */
    readonly contactId?: number;
}

/** POST /api/v1/intake-intelligence/requestor-search response shape (RequestorMatchResult). */
export interface RequestorMatchApiPayload {
    readonly matched: boolean;
    readonly requestorId?: number;
    readonly fullName?: string;
    readonly address1?: string;
    readonly address2?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zipCode?: string;
    readonly eTransfer: boolean;
    readonly national: boolean;
    readonly transactionCount: number;
    /**
     * Resolved contact (Spec #31605 follow-on). `contactId` links the existing contact
     * record and is the only contact value the SEARCH returns (AB#39052, Ali Moll on
     * PR !10513 thread 56655 - it is a requestor-level search).
     *
     * The demographics below are therefore NOT from the search response: they are
     * merged in by the row once it has resolved to one contact, from
     * GET requestors/{contactId}/contact, and they are already present when a
     * committed choice is rehydrated (buildPersistedRequestorMatch). Absent means "not
     * resolved yet or not resolvable" - never "this contact has no name".
     */
    readonly contactId?: number;
    readonly contactFirstName?: string;
    readonly contactLastName?: string;
    readonly email?: string;
    readonly phone?: string;
    readonly fax?: string;
    readonly matchMethod: "None" | "Fax" | "Email";
    readonly matchCount: number;
    readonly confidence: ApiMatchConfidence;
    readonly candidates?: readonly RequestorCandidateApiPayload[];
}

/**
 * Client-derived search signals (AC10/AC13/AC14) - NOT read from any backend
 * `RequestAnalysis.status`; these come from F1a's redux extraction state
 * (`requestAnalysis` present/absent, in-flight POST tracking, POST
 * error/timeout tracking) owned by the caller (the row components / their
 * container), not by this contract module.
 */
export interface MatchSearchSignals {
    /** False before any document is uploaded / extraction hasn't completed - no keys exist yet to search with (Business Rule 0). */
    readonly hasKeys: boolean;
    /**
     * True once a search/resolution has produced a result (auto match, manual
     * inline search, grid pick, or Create-New) - i.e. `apiResult` is populated.
     * Distinguishes the genuine empty state ("nothing entered yet" -> awaitingDocument)
     * from a value that WAS resolved even though the AI extracted no keys
     * (hasKeys=false). Without it a manually-resolved patient stays
     * awaitingDocument forever, so the inline search never collapses and
     * Confirm-All is permanently disabled (AB#31605). Optional; absent => false.
     */
    readonly searched?: boolean;
    /** True while the search POST for the current keys is in flight. */
    readonly isSearching: boolean;
    /** True when the search POST for the current keys errored or timed out (AC11). */
    readonly serviceUnavailable: boolean;
    /** True when the value was created/entered by the specialist (no-match "Create New") - not an AI match. Drives the blue user-entered badge (AC9). */
    readonly userEntered?: boolean;
    /**
     * True once THIS match was resolved by a human rather than by the automated search - the
     * specialist picked a candidate, or the row pre-resolved from the transaction's persisted
     * patient link (a link a human established earlier).
     *
     * Review finding on PR !9605 (BLOCKER-1). The first-name downgrade below was
     * provenance-blind: it re-fired on the specialist's own pick, because picking re-seeds the
     * match as "Single" while the record's first name still disagrees with the document. The row
     * dropped straight back to the chooser, the write-once link effect (gated on
     * confidence === "single") never fired, and the patient was never linked to the transaction -
     * leaving Create New, i.e. a duplicate patient record, as the only exit. The same blindness
     * un-pinned a human-confirmed link on every reopen, since the persisted match also arrives as
     * "Single" against a still-differing extracted name.
     *
     * The downgrade exists to stop the AUTOMATED search silently pinning the wrong person. Once a
     * human has decided, that protection has served its purpose and must not override them.
     * `differs.firstName` is still reported either way, so the disagreement stays visible.
     */
    readonly specialistResolved?: boolean;
}

function deriveStatus(signals: MatchSearchSignals, confidence: MatchConfidence): MatchStatus {
    if (signals.serviceUnavailable) {
        return "serviceUnavailable";
    }
    if (signals.isSearching) {
        return "matching";
    }
    // awaitingDocument is ONLY the genuine empty state: no AI-extracted keys AND
    // no search/resolution has happened yet. A manual search or grid pick sets
    // `searched` (via apiResult), so a value resolved without AI keys still
    // derives matched/noMatch and the inline search collapses (AB#31605).
    // serviceUnavailable/isSearching are checked first so a manual search that is
    // in flight or errored with no AI keys shows the right transient state
    // instead of falling back to awaitingDocument.
    if (!signals.hasKeys && !signals.searched) {
        return "awaitingDocument";
    }
    return confidence === "none" ? "noMatch" : "matched";
}

/** Case/whitespace-insensitive "Differs from record" compare (AC12/Business Rule 7). No differs signal when either side is missing. */
function differsFrom(recordValue: string | undefined, extractedValue: string | undefined): boolean {
    if (!recordValue || !extractedValue) {
        return false;
    }
    return recordValue.trim().toLowerCase() !== extractedValue.trim().toLowerCase();
}

/** Extracted (RequestAnalysisData) baseline for the patient Differs check (AC12 - lastName this cut; address/city have no comparable AI-extracted patient field). */
export interface PatientDiffersBaseline {
    readonly lastName?: string;
    /**
     * AB#35072: the AI-extracted patient GIVEN (first) name. The patient search itself cannot key on
     * it - IntakeController.PatientSearchAsync calls MatchPatient(facilityId, lastName, dob, mrn) and
     * has no given-name parameter - so two patients sharing Last Name + DOB are indistinguishable to
     * the query. The returned candidate DOES carry a first name, so we compare it here instead.
     */
    readonly firstName?: string;
}

/** Extracted (RequestAnalysisData) baseline for the requestor Differs check (AC12 - name/address/city this cut; fax/email Differs is deferred). */
export interface RequestorDiffersBaseline {
    readonly name?: string;
    readonly address1?: string;
    readonly city?: string;
}

/** Test 4.13: maps the patient-search response + client signals into the PatientMatchState contract F1d reads. */
export function mapPatientMatchState(
    api: PatientMatchApiPayload | undefined,
    signals: MatchSearchSignals,
    baseline: PatientDiffersBaseline = {},
): PatientMatchState {
    const rawConfidence = toClientConfidence(api?.confidence);
    const firstNameDiffers = differsFrom(api?.patient?.contact?.firstName, baseline.firstName);
    // AB#35072: a single match whose FIRST NAME disagrees with the document is not a safe
    // auto-resolution - the search keys only on lastName + dob (the endpoint has no given-name
    // parameter), so two people sharing Last Name + DOB collapse to one "single" match and the wrong
    // patient would be silently pinned with the specialist never seeing the disagreement.
    // Downgrading to "multiple" reuses the existing needs-attention path: the row stops
    // auto-confirming and the specialist picks. Deliberately does NOT fabricate a match or guess -
    // same never-guess stance as request type/purpose (AB#35098).
    // Only an UNCONFIRMED, automated single match is downgraded - see specialistResolved.
    const confidence: MatchConfidence =
        rawConfidence === "single" && firstNameDiffers && !signals.specialistResolved
            ? "multiple"
            : rawConfidence;
    const status = deriveStatus(signals, confidence);

    return {
        status,
        matched: api?.matched ?? false,
        matchMethod: api?.matchMethod ?? "None",
        matchCount: api?.matchCount ?? 0,
        confidence,
        differs: {
            firstName: firstNameDiffers,
            lastName: differsFrom(api?.patient?.contact?.lastName, baseline.lastName),
            address: false,
            city: false,
        },
        serviceUnavailable: signals.serviceUnavailable,
        userEntered: signals.userEntered ?? false,
        // AB#35071: carry the resolved id alongside the match. Same source PatientInfoRow's
        // onSelectPatient uses (candidates[0] is the matched record on a single match - see
        // PatientManager, which populates Candidates for every confidence, not just Multiple).
        patientId: api?.matched && confidence === "single" ? api?.candidates?.[0]?.patientId : undefined,
    };
}

/** Test 4.13: maps the requestor-search response + client signals into the RequestorMatchState contract F1d reads. */
export function mapRequestorMatchState(
    api: RequestorMatchApiPayload | undefined,
    signals: MatchSearchSignals,
    baseline: RequestorDiffersBaseline = {},
): RequestorMatchState {
    const confidence = toClientConfidence(api?.confidence);
    const status = deriveStatus(signals, confidence);

    return {
        status,
        matched: api?.matched ?? false,
        matchMethod: api?.matchMethod ?? "None",
        matchCount: api?.matchCount ?? 0,
        confidence,
        eTransfer: api?.eTransfer ?? false,
        transactionCount: api?.transactionCount ?? 0,
        differs: {
            name: differsFrom(api?.fullName, baseline.name),
            address: differsFrom(api?.address1, baseline.address1),
            city: differsFrom(api?.city, baseline.city),
        },
        serviceUnavailable: signals.serviceUnavailable,
        userEntered: signals.userEntered ?? false,
    };
}
